import {
  Box,
  Button,
  Flex,
  Link,
  ListItem,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';
import { sidePaneMolecule } from '@web-app/atoms/in-session/sidePaneMolecule';
import { SidePaneTabId } from '@web-app/components/CallV2/sidePaneTabIds';
import {
  PARTNER_TAG_LINKS,
  TAGS,
} from '@web-app/components/PatientTags/constants';
import { SessionReminderModal } from '@web-app/components/SessionReminderModal';
import { useWalmartShoppingInsights } from '@web-app/utils/feature-flags/walmart-shopping-insights';
import { useMolecule } from 'bunshi/react';
import { useSetAtom } from 'jotai';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef } from 'react';

const linkSx = {
  fontWeight: 'bold',
  textDecoration: 'underline',
  _hover: { textDecoration: 'underline' },
};

const WalmartReminderModal: React.FC = () => {
  const showWalmartInsights = useWalmartShoppingInsights();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const gotItButtonRef = useRef<HTMLButtonElement>(null);
  const { activeSidePaneTabAtom } = useMolecule(sidePaneMolecule);
  const setActiveTab = useSetAtom(activeSidePaneTabAtom);

  useEffect(() => {
    if (showWalmartInsights) {
      onOpen();
    }
  }, [onOpen, showWalmartInsights]);

  const handlePatientTabClick = useCallback(() => {
    setActiveTab(SidePaneTabId.PATIENT);
    onClose();
  }, [onClose, setActiveTab]);

  if (!showWalmartInsights) {
    return null;
  }

  return (
    <SessionReminderModal
      popupId="walmart-patient-reminders"
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      initialFocusRef={gotItButtonRef}
    >
      <ModalOverlay />
      <ModalContent
        maxW="452px"
        borderRadius="lg"
        bg="bg.default"
        color="text.default"
        p={0}
        overflow="visible"
      >
        <Flex direction="column" gap={6} px={6} pt="42px" pb={6}>
          <ModalHeader display="flex" alignItems="flex-start" gap={3} p={0}>
            <Box flexShrink={0}>
              <Image
                src="/icons/walmart-spark.svg"
                alt=""
                width={26}
                height={30}
                aria-hidden
              />
            </Box>
            <Text
              as="span"
              fontSize="22px"
              fontWeight="black"
              lineHeight="normal"
            >
              New! Walmart Insights Available
            </Text>
          </ModalHeader>

          <Box px={5}>
            <UnorderedList
              styleType="disc"
              ml={6}
              fontSize="md"
              fontWeight="medium"
              lineHeight="1.5"
            >
              <ListItem>
                If a patient has consented, they&apos;re enrolled in our Walmart
                Everyday Health Signals
                <Text as="sup" fontSize="0.65em">
                  SM
                </Text>{' '}
                program and have agreed to share their shopping insights.
              </ListItem>
              <ListItem>
                Use these health-related insights to understand purchasing
                patterns and personalize your recommendations - check the{' '}
                <Link as="button" onClick={handlePatientTabClick} sx={linkSx}>
                  patient tab
                </Link>{' '}
                to see if they&apos;re available for this patient. Note - not
                all patients may have available insights.
              </ListItem>
              <ListItem>
                Learn more about the Walmart partnership{' '}
                <Link
                  href={PARTNER_TAG_LINKS[TAGS.WALMART.toLowerCase()]}
                  isExternal
                  sx={linkSx}
                >
                  here.
                </Link>
              </ListItem>
            </UnorderedList>
          </Box>

          <Flex direction="column" gap="6px" pt={2}>
            <Button
              ref={gotItButtonRef}
              w="full"
              h="auto"
              p="10px"
              onClick={onClose}
              bg="#1377C4"
              color="text.inverse"
              borderRadius="md"
              fontSize="md"
              fontWeight="bold"
              _hover={{ bg: '#1169AD' }}
              _active={{ bg: '#0F5E99' }}
            >
              Got it!
            </Button>
          </Flex>
        </Flex>
      </ModalContent>
    </SessionReminderModal>
  );
};

export default WalmartReminderModal;
